import { DateTime } from 'luxon';
import { Entity } from '../../entity';
import { VisitStatus } from '../../enums';
import { EntityViewFilter } from '../../entities/views/entity.view.filter';
import { EntityViewSort } from '../../entities/views/entity.view';
import { TaskRequirementFlags } from '../../tasks/task.evidence';
import { MAX_OVERDUE_GRACE_DAYS } from '../../settings/planned.work.settings';
import { TaskApprovalStatus, TaskApprovalStanding, VisitTaskApprovalSummary } from './task.approval';
import { heldByPerson, isFullyApproved } from './task.approval.policy';
import { JobConstructionAuthorisationKind } from './job.construction.evidence';
import { isLocalDatePaused, ServicePlanPausedDays } from '../service.plan/service.plan.paused.interval';
import type { ServicePlanStatusSystemName } from '../service.plan/service.plan.status.system.name';
import type { TaskApprovalSource, VisitConstructionFailure } from './visit.instance';
import type { ContactEntity } from '../../contact/contact.options';
import type { PlannedTaskConstructionRule } from '../planned.task/planned.task';
import type { VisitSpacingFacts } from './visit.spacing';
import type { VisitJobSummary } from './visit.job.summary';
import type { ScheduleWeekCycle } from '../../../scheduling/schedule.rule';

export enum VisitBrowseState {
    Projected = 'Projected',
    Booked = 'Booked',
    JobCreated = 'Job created',
}

export enum VisitBrowseMode {
    Operational = 'Operational',
    PlanPreview = 'Plan preview',
}

/** User-facing attendance status, independent of whether its forecast has a stored record. */
export enum VisitDisplayStatus {
    Planned = 'Planned',
    Modified = 'Planned · Modified',
    Rescheduled = 'Planned · Rescheduled',
    Skipped = 'Skipped',
    Scheduled = 'Scheduled',
    InProgress = 'In progress',
    OnHold = 'On hold',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export enum VisitAssignmentSource {
    Recorded = 'Recorded',
    Unassigned = 'Unassigned',
    Inherited = 'Inherited',
    Overridden = 'Overridden',
}

export enum VisitContentSource {
    Snapshot = 'Recorded snapshot',
    Projection = 'Projection',
    PlanAndOverrides = 'Plan and overrides',
    Job = 'Job',
}

export enum VisitBrowseCommandId {
    Dispatch = 'VisitDispatchCommand',
    Skip = 'VisitSkipCommand',
    Restore = 'VisitRestoreCommand',
    RetryJobCreation = 'VisitRetryJobCreationCommand',
}

/** Why the Job stamper has not yet built this occurrence's Job (WBS-11652). */
export enum VisitConstructionAttentionState {
    /** Failed and backing off; the stamper will try again at `nextAttemptAt`. */
    Retrying = 'Retrying',
    /** Healthy but held by dry run, the kill switch or the daily limit; resumes by itself when lifted. */
    Held = 'Held',
    /** Quarantined: the stamper no longer selects it until a person retries or its source changes. */
    NeedsAttention = 'NeedsAttention',
    /** Auto-approved and its appointment passed with no Job: the stamper owed it one, so it is a construction problem, not a miss. */
    NotCreated = 'NotCreated',
}

/**
 * Why a Visit will not get its Job, or its Job will not finish, unless a person acts (WBS-12652/12653): the
 * one value the Visits board's Needs attention view shows and filters on. Derived on read by `visitNeedsAttention`.
 */
export enum VisitNeedsAttentionReason {
    /** Upcoming, and its line's Job defaults leave the Job without a price book, owner or contact. */
    CannotCreateJob = 'CannotCreateJob',
    /** The stamper parked it (`VisitConstructionAttentionState.NeedsAttention`) until a person retries. */
    JobCreationFailed = 'JobCreationFailed',
    /** Owed a Job and its appointment passed without one (`VisitConstructionAttentionState.NotCreated`). */
    NotCreated = 'NotCreated',
    /** Attended to, but its Job is unfinished past the appointment (`visitJobOverdue`, the Job overdue view's rule). */
    JobNotFinished = 'JobNotFinished',
    /** Its approved work changed, so the approval blocks the Job until someone approves it again. */
    ChangedSinceApproval = 'ChangedSinceApproval',
}

/** How far ahead "Can't create job" looks: far enough to fix a line before its first Visit is stamped. */
export const VISIT_NEEDS_ATTENTION_LEAD_DAYS = 14;

export interface VisitConstructionAttention {
    state: VisitConstructionAttentionState;
    reason?: string;
    attempts?: number;
    lastAttemptAt?: Date;
    nextAttemptAt?: Date;
}

/** Read projection shared by the Visits board, calendar and record surfaces. */
export interface VisitBrowseRow extends Entity, Partial<TaskApprovalStanding> {
    occurrenceId: string;
    visitId?: string;
    number: string;
    state: VisitBrowseState;
    displayStatus?: VisitDisplayStatus;
    mode: VisitBrowseMode;
    status: VisitStatus | VisitBrowseState.Projected;
    servicePlan?: Entity;
    serviceLine?: Entity;
    /** Current canonical Service Line classifications, independent of browse/customer scope. */
    serviceType?: Entity | null;
    serviceScope?: Entity | null;
    customer?: Entity;
    site?: Entity;
    timezone: string;
    localDate: string;
    /** Who approved the stored row; absent on a forecast row, which is never approved. Age reads it before standing is joined. */
    approvalSource?: TaskApprovalSource | null;
    /** Section 15 compliance window, local dates. Equal to `localDate` on a Visit with no leeway task. */
    windowStart?: string;
    windowEnd?: string;
    /** "due 15 Dec · can be done from 15 Nov"; present only on a leeway Visit. */
    complianceWindowLabel?: string;
    bookedDate: string;
    startDate: Date;
    endDate: Date;
    allDay: boolean;
    resources: Entity[];
    contractor?: Entity;
    assignment: VisitAssignmentSource;
    overridden: boolean;
    datesOverridden?: boolean;
    /** Distinct move operations that placed this Visit's work, counted from task history (undo and reset excluded). */
    moveCount?: number;
    /** Latest field-task completion stamped on the stored Visit; see `VisitInstance.completedAt`. */
    completedAt?: Date;
    /** Section 16: each spaced task's nearest neighbours on the line, for the reschedule warning. Derived on read. */
    spacing?: VisitSpacingFacts[];
    /** The occurrence left the plan's schedule after the Visit was stored (stored as `reviewConflict`); `reviewReason` says why. */
    noLongerScheduled: boolean;
    reviewReason?: string;
    /**
     * When the current date override was decided. Only a decision taken at or after the effective
     * window end counts as deciding a passed appointment; see `visitAppointmentDecided`.
     */
    dateDecidedAt?: Date;
    /** Local date the owning plan was activated; occurrences before it never age. Filled by the reader. */
    planActivatedOn?: string;
    /** The owning plan is not running. Derived on read from the plan; pausing writes nothing to the Visit. */
    planPaused?: boolean;
    /**
     * The owning plan's lifecycle status, derived on read. Only a Started plan's work may be moved, skipped,
     * restored, undone or reset; the web reads this to say why those actions are not offered.
     */
    planStatus?: ServicePlanStatusSystemName;
    /** Local days the owning plan was paused; occurrences inside never age. Filled by the reader. */
    planPausedDays?: ServicePlanPausedDays[];
    /** Derived on read by `visitAge`; never stored. */
    ageState?: VisitAgeState;
    /** Local days since the appointment day, present only while Overdue or Missed. */
    ageDays?: number;
    /** Local date the Visit turns (or turned) Missed, present only while Overdue or Missed. */
    missedOn?: string;
    /** Derived on read by `visitAge`: auto-approved, passed, and still Job-less. Stays Current; see `VisitConstructionAttentionState.NotCreated`. */
    jobNotCreated?: boolean;
    /** Derived on read by `visitJobOverdue`; never stored. Not an age: the Visit was attended to, its Job is late. */
    jobOverdue?: boolean;
    /** The owning Service Line's review owner; the path to whoever resolves an overdue Visit. */
    lineOwner?: ContactEntity;
    job?: Entity;
    /** The Job's crew and appointment; present only on a single-Visit read of a Visit with a Job. */
    jobSummary?: VisitJobSummary;
    /**
     * Why the stamper has not built this Visit's Job, as one filterable value. Present only while the
     * stamper is failing, holding or owed the Job; absent once the Job exists. Derived on read, never stored.
     */
    jobCreation?: VisitConstructionAttentionState;
    /** The detail behind `jobCreation` (reason, attempts, timing), for the tag tooltip. */
    jobCreationIssue?: VisitConstructionAttention;
    /**
     * What a Job for this Job-less Visit would still lack (WBS-12662), as one readable line ("Needs a price
     * book, a contact"); absent when its Job can be built. Derived on read from the line, site and customer.
     */
    jobReadiness?: string;
    /** The same gaps with where to fix each, for the tag tooltip and the Create job reason. */
    jobReadinessDetail?: string[];
    /**
     * The stored `VisitInstance.constructionFailure` of a Job-less Visit (WBS-12696). It never disables
     * Create job: creating the Job by hand is how a person retries it.
     */
    jobCreationFailure?: VisitConstructionFailure;
    /**
     * Why this Visit needs a person (no Job coming, or its Job late), or absent when nothing does. Derived on
     * read by `visitNeedsAttention` from `jobCreation`, `jobOverdue`, `approvalStatus` and `jobReadinessDetail`.
     */
    needsAttention?: VisitNeedsAttentionReason;
    /**
     * Week A or B of the line's fortnight on the day `startDate` falls in, in the Visit's zone (WBS-12805). Present
     * only when the line has a fortnightly A/B task; derived on read by `fortnightWeekCycle`, never stored.
     */
    weekCycle?: ScheduleWeekCycle;
    taskCount?: number;
    /** A stored Visit's task-row approval counts, derived on read from the rows the board hydrates; absent on a forecast. */
    approvalSummary?: VisitTaskApprovalSummary;
    detailsLoaded: boolean;
    contentSource: VisitContentSource;
    tasks: VisitBrowseTask[];
    items: VisitBrowseItem[];
    bookings: VisitBrowseBooking[];
    trace: string[];
}

const EXECUTION_DISPLAY_STATUS: Partial<Record<VisitStatus, VisitDisplayStatus>> = {
    Scheduled: VisitDisplayStatus.Scheduled,
    InProgress: VisitDisplayStatus.InProgress,
    OnHold: VisitDisplayStatus.OnHold,
    Completed: VisitDisplayStatus.Completed,
    Cancelled: VisitDisplayStatus.Cancelled,
};

/** Once a Job exists its projected status is the display; only a Job-less Planned Visit shows exceptions. */
export function visitDisplayStatus(row: Pick<VisitBrowseRow, 'job' | 'status' | 'overridden' | 'datesOverridden'>): VisitDisplayStatus {
    const execution = EXECUTION_DISPLAY_STATUS[row.status as VisitStatus];
    if (execution) return execution;
    if (row.job?._id) return VisitDisplayStatus.Scheduled;
    if (row.status === 'Skipped') return VisitDisplayStatus.Skipped;
    if (row.datesOverridden) return VisitDisplayStatus.Rescheduled;
    return row.overridden ? VisitDisplayStatus.Modified : VisitDisplayStatus.Planned;
}

/** Shared presentation gate. Domain commands revalidate current records before changing execution. */
export function visitBrowseCapabilities(row: VisitBrowseRow, now: Date = new Date()) {

    const operational = row.mode === VisitBrowseMode.Operational && row.active !== false && !row.job?._id;
    const booked = operational && row.state === VisitBrowseState.Booked && !!row.visitId && row.status === 'Planned';
    const projected = operational && row.state === VisitBrowseState.Projected && !row.visitId;
    return {
        // Untouched occurrences are forecast only (WBS-11962), so "Create job now" must reach them too; the
        // strategy saves the row first, as Skip does.
        // Only authorised work is offered (WBS-11652 gate): the server refuses anything else, so the action
        // is hidden rather than shown and then refused. Missed work must be rescheduled first.
        dispatch: (booked || projected) && row.detailsLoaded && ((row.taskCount ?? 0) > 0 || row.items.length > 0) && !row.noLongerScheduled
            && isFullyApproved(row.approvalStatus) && row.ageState !== VisitAgeState.Missed && !row.planPaused,
        skip: booked || projected,
        restore: operational && !!row.visitId && row.status === 'Skipped',
        // Dispatch stays offered: the command asks for the date decision rather than hiding the action.
        needsDateDecision: (booked || projected) && visitAppointmentPassed(row, now) && !visitAppointmentDecided(row),
        // Only quarantined work: a Retrying occurrence is already queued, so a manual retry would just race it.
        retryJobCreation: operational && row.jobCreation === VisitConstructionAttentionState.NeedsAttention,
    };
}

/** What `visitNeedsAttention` reads off a row: the overlays it combines and the appointment it times. */
export type VisitNeedsAttentionSubject = VisitAppointment & Pick<VisitBrowseRow, 'job' | 'status' | 'jobCreation' | 'jobOverdue' | 'approvalStatus' | 'jobReadinessDetail'> & Partial<Pick<VisitBrowseRow, 'active'>>;

/**
 * The one reason a Visit needs a person, by what most needs doing first: a passed appointment with no Job,
 * then a Job unfinished past its appointment, then a parked stamp, then an approval that no longer covers the
 * work, then a line that cannot build Jobs for an appointment inside the lead. A Visit with several problems
 * shows the first, so filtering by a later reason leaves it out; fixing the first reveals the next. A Visit
 * with a Job can only have the Job's reason: the others are all about a Job not yet built.
 */
export function visitNeedsAttention(row: VisitNeedsAttentionSubject, now: Date): VisitNeedsAttentionReason | undefined {

    if (row.active === false || row.status === 'Skipped') return undefined;
    if (!row.job?._id && row.jobCreation === VisitConstructionAttentionState.NotCreated) return VisitNeedsAttentionReason.NotCreated;
    if (row.job?._id) return row.jobOverdue ? VisitNeedsAttentionReason.JobNotFinished : undefined;
    if (row.jobCreation === VisitConstructionAttentionState.NeedsAttention) return VisitNeedsAttentionReason.JobCreationFailed;
    if (row.approvalStatus === TaskApprovalStatus.NeedsReview) return VisitNeedsAttentionReason.ChangedSinceApproval;
    if (!row.jobReadinessDetail?.length) return undefined;
    // A passed appointment is the age rules' to report; past the lead there is still time to fix the line.
    if (visitAppointmentPassed(row, now)) return undefined;
    const leadEnd = now.getTime() + VISIT_NEEDS_ATTENTION_LEAD_DAYS * DAY_MILLIS;

    return new Date(row.startDate).getTime() < leadEnd ? VisitNeedsAttentionReason.CannotCreateJob : undefined;
}

/** Rows in, the same rows out with `needsAttention` set where it applies; a pure read of fields already overlaid. */
export function withVisitNeedsAttention<T extends VisitBrowseRow>(rows: T[], now: Date): T[] {

    return rows.map(row => {

        const reason = visitNeedsAttention(row, now);
        return reason ? { ...row, needsAttention: reason } : row;
    });
}

const DAY_MILLIS = 24 * 60 * 60 * 1000;

/**
 * How long a job-less past occurrence stays readable. Aligned with the planned-task horizon's
 * retention: past it the stored due days are dropped, so a read older than this would show a Visit
 * the forecast can no longer reproduce. Grace is capped below it so every Visit is Missed for a while.
 */
export const VISIT_PAST_RETENTION_DAYS = 35;

/** Derived only, never stored: an attendance whose appointment passed without a Job. */
export enum VisitAgeState {
    Current = 'Current',
    Overdue = 'Overdue',
    Missed = 'Missed',
}

export enum VisitConstructionBlock {
    NotApproved = 'NotApproved',
    AppointmentPassed = 'AppointmentPassed',
}

/** The appointment facts age and construction read; a Visit record maps onto it as easily as a row. */
export type VisitAppointment = Pick<VisitBrowseRow, 'startDate' | 'endDate' | 'allDay' | 'timezone'>;
export type VisitAgeSubject = VisitAppointment & Pick<VisitBrowseRow, 'status' | 'job' | 'planActivatedOn' | 'planPausedDays' | 'approvalStatus'> & Partial<Pick<VisitBrowseRow, 'windowEnd' | 'localDate' | 'approvalSummary'>>;
export type VisitDecidedAppointment = VisitAppointment & Pick<VisitBrowseRow, 'dateDecidedAt'>;

export interface VisitAge {
    ageState: VisitAgeState;
    ageDays?: number;
    missedOn?: string;
    jobNotCreated?: boolean;
}

export interface VisitConstructionGate {
    allowed: boolean;
    block?: VisitConstructionBlock;
}

/**
 * The zone a Visit's instants are named in: its own, else the tenant's, else UTC. Never the process or
 * browser zone — that would make a server in UTC and a browser in Perth disagree about the same Visit's day.
 */
export function resolveVisitZone(timezone: string | undefined, tenantZone?: string): string {

    return timezone || tenantZone || 'UTC';
}

/** The local day the appointment belongs to; a zero-length window still belongs to its start day. */
function appointmentDay(row: VisitAppointment): DateTime {

    const start = new Date(row.startDate).getTime();
    const lastInstant = Math.max(start, new Date(row.endDate).getTime() - 1);
    return zonedDayStart(lastInstant, resolveVisitZone(row.timezone));
}

function localToday(row: Pick<VisitAppointment, 'timezone'>, now: Date): DateTime {

    return zonedDayStart(now.getTime(), resolveVisitZone(row.timezone));
}

/**
 * The last local day resolved per zone, as the instant range it covers.
 *
 * Aging a board page asks for the local day of thousands of instants that almost all fall on a few
 * days, and each luxon zone conversion costs tens of microseconds — 200 ms a page on the 5k/day seed.
 * An instant inside `[from, to)` has exactly that day, so the cached value is the one luxon would
 * return. Luxon values are immutable, so sharing one is safe.
 */
const lastZonedDay = new Map<string, { from: number; to: number; day: DateTime }>();

function zonedDayStart(millis: number, zone: string): DateTime {

    const cached = lastZonedDay.get(zone);
    if (cached && millis >= cached.from && millis < cached.to) return cached.day;
    const day = DateTime.fromMillis(millis, { zone }).startOf('day');
    if (!day.isValid) return day;
    if (lastZonedDay.size > ZONED_DAY_CACHE_LIMIT) lastZonedDay.clear();
    lastZonedDay.set(zone, { from: day.toMillis(), to: day.plus({ days: 1 }).toMillis(), day });
    return day;
}

const ZONED_DAY_CACHE_LIMIT = 256;

/** When the appointment stops being today's work: its window end, or for all-day the end of its local day. */
function appointmentEnd(row: VisitAppointment): number {

    return row.allDay
        ? appointmentDay(row).plus({ days: 1 }).toMillis()
        : new Date(row.endDate).getTime();
}

export function visitAppointmentPassed(row: VisitAppointment, now: Date): boolean {

    return now.getTime() >= appointmentEnd(row);
}

/**
 * A decision taken before the window ended was about a future date, so it cannot settle a passed one.
 * Measured against the same end as `visitAppointmentPassed`, or an all-day Visit decided late on its own
 * day would count as passed yet decided, and be stamped for a day already over.
 */
export function visitAppointmentDecided(row: VisitDecidedAppointment): boolean {

    return !!row.dateDecidedAt && new Date(row.dateDecidedAt).getTime() >= appointmentEnd(row);
}

function ageExempt(row: VisitAgeSubject): boolean {

    return !!row.job?._id || (row.status !== 'Planned' && row.status !== VisitBrowseState.Projected);
}

/**
 * Age of a job-less Planned attendance. Grace 0 goes straight to Missed. An appointment before the
 * plan's activation day never ages: those dates predate the contract running, so nobody missed them.
 * A day the plan was paused never ages either, for the same reason. An auto-approved Visit never ages:
 * the stamper owed it a Job, so a passed one without is `jobNotCreated`, not Overdue or Missed. Nor does an
 * unapproved one: nobody committed to it, so a passed date leaves it awaiting approval, not late.
 */
export function visitAge(row: VisitAgeSubject, now: Date, graceDays: number, autoApproved = row.approvalStatus === TaskApprovalStatus.AutoApproved): VisitAge {

    if (ageExempt(row) || !visitAppointmentPassed(row, now)) return { ageState: VisitAgeState.Current };
    const day = appointmentDay(row);
    const facts = dayFacts(day);
    facts.iso ??= day.toISODate()!;
    if (row.planActivatedOn && facts.iso < row.planActivatedOn) return { ageState: VisitAgeState.Current };
    if (isLocalDatePaused(row.planPausedDays, facts.iso)) return { ageState: VisitAgeState.Current };
    if (autoApproved) return { ageState: VisitAgeState.Current, jobNotCreated: true };
    if (!isApprovedForAge(row)) return { ageState: VisitAgeState.Current };
    const grace = Math.min(MAX_OVERDUE_GRACE_DAYS, Math.max(0, Math.floor(Number.isFinite(graceDays) ? graceDays : 0)));
    const today = localToday(row, now);
    // Section 15: a leeway Visit still counts until its window closes, so it ages from `windowEnd`, not the appointment.
    // Only a real leeway: a no-leeway Visit moved before its due date still ages from its appointment.
    if (row.windowEnd && row.localDate && row.windowEnd > row.localDate && row.windowEnd > facts.iso) return windowAge(row.windowEnd, today, grace);
    const key = `${today.toMillis()}|${grace}`;
    let aged = facts.ages.get(key);
    if (!aged) {
        aged = { ageDays: Math.max(0, Math.round(today.diff(day, 'days').days)), missedOn: day.plus({ days: grace }).toISODate()! };
        facts.ages.set(key, aged);
    }
    return {
        ageState: aged.ageDays < grace ? VisitAgeState.Overdue : VisitAgeState.Missed,
        ageDays: aged.ageDays,
        missedOn: aged.missedOn,
    };
}

/**
 * A Visit someone approved any of, even if that approval is now being rechecked: its date was still committed to.
 * A joined standing wins; before the join, a stored Visit's task-approval summary is its standing.
 */
function isApprovedForAge(row: VisitAgeSubject): boolean {

    return heldByPerson(row.approvalStatus ?? row.approvalSummary?.status);
}

function windowAge(windowEnd: string, today: DateTime, grace: number): VisitAge {

    const closes = DateTime.fromISO(windowEnd, { zone: today.zone }).startOf('day');
    const ageDays = Math.round(today.diff(closes, 'days').days);
    if (ageDays <= 0) return { ageState: VisitAgeState.Current };
    return {
        ageState: ageDays < grace ? VisitAgeState.Overdue : VisitAgeState.Missed,
        ageDays,
        missedOn: closes.plus({ days: grace }).toISODate()!,
    };
}

/** What `visitAge` derives from one shared day value, so rows on the same day and zone derive it once. */
const agesByDay = new WeakMap<DateTime, { iso?: string; ages: Map<string, { ageDays: number; missedOn: string }> }>();

function dayFacts(day: DateTime): { iso?: string; ages: Map<string, { ageDays: number; missedOn: string }> } {

    let facts = agesByDay.get(day);
    if (!facts) {
        facts = { ages: new Map() };
        agesByDay.set(day, facts);
    }
    return facts;
}

/**
 * A Job'd Visit's statuses while its Job still runs. The Job status projection writes the others,
 * Completed and Cancelled, when it ends; a Planned status with a Job is a link the projection has not
 * caught up with, so it is still open. The board's indexed read pins this same list.
 */
export const VISIT_JOB_OPEN_STATUSES: readonly VisitStatus[] = ['Planned', 'Scheduled', 'InProgress', 'OnHold'];

export type VisitJobOverdueSubject = VisitAppointment & Pick<VisitBrowseRow, 'status' | 'job'>;

/**
 * A Visit whose Job exists but has not finished by the end of its appointment: the window end, or for
 * an all-day Visit the end of its local day in its own zone. Separate from `visitAge` on purpose: the
 * Visit was not missed (it has a Job), so this never counts as missed attendance or ages the Visit.
 */
export function visitJobOverdue(row: VisitJobOverdueSubject, now: Date): boolean {

    return !!row.job?._id && VISIT_JOB_OPEN_STATUSES.includes(row.status as VisitStatus) && visitAppointmentPassed(row, now);
}

export function visitAgeState(row: VisitAgeSubject, now: Date, graceDays: number, autoApproved?: boolean): VisitAgeState {

    return visitAge(row, now, graceDays, autoApproved).ageState;
}

/**
 * Dropped on read, nothing written. Keyed on the START day because the read floor and its index filter
 * on the start date; Job'd and Skipped Visits stay reachable at any age.
 */
export function isVisitGone(row: VisitAgeSubject, now: Date): boolean {

    if (ageExempt(row)) return false;
    // The floor is a local day start, so "the row's day starts before it" is "the row starts before it".
    // Comparing instants leaves the zone's day cache on today; resolving the row's own day per row made
    // today and the row's day evict each other, two luxon conversions a row — 250 ms on a 5k-row window.
    return new Date(row.startDate).getTime() < goneFloor(resolveVisitZone(row.timezone), now);
}

/** Today's retention floor per zone, as an instant; NaN for an invalid zone, which no comparison passes. */
const goneFloors = new Map<string, { from: number; to: number; floor: number }>();

function goneFloor(zone: string, now: Date): number {

    const at = now.getTime();
    const cached = goneFloors.get(zone);
    if (cached && at >= cached.from && at < cached.to) return cached.floor;
    const today = zonedDayStart(at, zone);
    if (!today.isValid) return Number.NaN;
    if (goneFloors.size > ZONED_DAY_CACHE_LIMIT) goneFloors.clear();
    const floor = today.minus({ days: VISIT_PAST_RETENTION_DAYS }).toMillis();
    goneFloors.set(zone, { from: today.toMillis(), to: today.plus({ days: 1 }).toMillis(), floor });
    return floor;
}

/** Construction needs standing approval, and a passed appointment needs a date decided after it passed. */
export function visitConstructionGate(row: VisitDecidedAppointment, now: Date, standing: TaskApprovalStatus | null | undefined): VisitConstructionGate {

    if (!isFullyApproved(standing)) {
        return { allowed: false, block: VisitConstructionBlock.NotApproved };
    }
    if (visitAppointmentPassed(row, now) && !visitAppointmentDecided(row)) {
        return { allowed: false, block: VisitConstructionBlock.AppointmentPassed };
    }
    return { allowed: true };
}

export interface VisitBrowseTask extends TaskRequirementFlags {
    _id: string;
    name: string;
    description?: string;
    source?: string;
    /** The rule captured with approval evidence, so snapshot and history views can show it. */
    constructionRule?: PlannedTaskConstructionRule;
    /** Identity localDate by which a statutory task must be done on this occurrence. */
    statutoryDueDate?: string;
    /** A subtask line (resolved and stored Visit work carry it); absent reads as a top-level task. */
    isSubtask?: boolean;
    /** The Visit task row (`planned_visit_tasks._id`) behind this task; `_id` stays the source task. Absent on forecast and Job tasks. */
    visitTaskId?: string;
    /** Local date the plan places this task on; the rescheduled date is the Visit's when it differs. */
    scheduledDate?: string;
    /** The row's revision, frozen into a reset or move selection so a concurrent change is refused, not overwritten. */
    revision?: number;
    /** Local date the row's latest move or undo took it from; absent when never moved, reset, or moved within a day. */
    movedFrom?: string;
    /** Who authorised this task: the row's own manual approval, else the line's auto-approval. Absent while unapproved. */
    approval?: {
        kind: JobConstructionAuthorisationKind.Manual | JobConstructionAuthorisationKind.AutoApproved;
        /** Manual only: the decision's current validity. */
        state?: TaskApprovalStatus;
        by?: Entity;
        at?: Date;
    };
}

export interface VisitBrowseItem {
    _id: string;
    name: string;
    quantity?: number;
}

export interface VisitBrowseBooking {
    _id: string;
    resource?: Entity;
    startDate: Date;
    endDate: Date;
}

/** Embedded Visit view sets a mount picks by `header.variant`; without one it gets the shared embedded views. */
export enum VisitSystemViewVariant {
    /** The Service Plan and Service Line Visits tabs: one plain view, so the attendance tiles count the whole period. */
    Attendance = 'Attendance',
    /** The Customer and Site Visits tabs: the same plain view, plus Site and Service Plan columns, since their Visits span several. */
    AccountAttendance = 'AccountAttendance',
}

export enum VisitBrowseScope {
    Upcoming = 'Upcoming',
    History = 'History',
    Period = 'Period',
}

/** The last row of a keyset page: everything strictly after it, in `startDate` then `occurrenceId` order. */
export interface VisitBrowseKeysetCursor { startDate: string; occurrenceId: string; }

/** Bounded projections and persisted attendance are filtered and paged by the Visit domain. */
export interface VisitBrowsePageRequest {
    scope: VisitBrowseScope;
    timezone: string;
    servicePlanId?: string;
    serviceLineId?: string;
    customerId?: string;
    siteId?: string;
    from?: string;
    to?: string;
    calendarRange?: boolean;
    filters?: EntityViewFilter[];
    search?: string;
    sort?: EntityViewSort;
    page?: number;
    pageSize?: number;
    /**
     * KEYSET paging for the `startDate` order, as an alternative to `page`.
     *
     * An offset page shifts whenever a row is inserted or removed before it, so the same request run
     * twice can duplicate a row or skip one. This names the last row of the previous page instead, and
     * the next page is everything strictly after `(startDate, occurrenceId)` in the board's own order.
     * A row inserted before the cursor simply is not in that range, so a shift cannot be observed.
     *
     * `occurrenceId` is the tiebreak because it is the identity a projected and a persisted row agree
     * on; two occurrences sharing a start time still page deterministically. Present `after` replaces
     * `page`; every other consumer keeps offset paging.
     */
    after?: VisitBrowseKeysetCursor;
    unpaged?: boolean;
    /** False keeps paused/draft plan reads on stored attendance, without invoking plan-preview projections. */
    includePlanPreview?: boolean;
    /** Start-date pages can omit projected task hydration when the caller only needs occurrence identities. */
    includeTaskDetails?: boolean;
}

/**
 * One page of the Visits board.
 *
 * WBS-11112 — WHICH SCOPE SETS WHICH IS THE POINT.
 *
 * `History` is persisted, finite and paged by Mongo, so it sets an exact `total` and no `hasMore`.
 * `Upcoming` and `Period` are FORECASTS: the rows come from a walk over a projected twelve-month
 * window merged with the window's stored exceptions, and counting the projection side meant grouping
 * the whole window — ~3 s of a 4.5 s page, reporting 11,267,372, which was a TASK count displayed on a
 * paginator of visits. Those scopes set `hasMore` and omit `total`.
 *
 * A consumer must therefore prefer `total` when present and fall back to `hasMore` when it is absent,
 * rather than assuming either is always there.
 */
export interface VisitBrowsePage {
    items: VisitBrowseRow[];
    total?: number;
    hasMore?: boolean;
    page: number;
    pageSize: number;
    projectionEnd: string;
}
